import type { PermissionState } from '@capacitor/core';

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { create } from 'zustand';

import { getNotificationFindQueryKey, notificationUpdate } from '@/api/gen-new/notification/notification';
import { pushDeviceRegister } from '@/api/gen-new/push/push';
import { getNativeAppInfo } from '@/appUpdate/nativeAppInfo';
import { getSession } from '@/auth/AuthSession';
import { isAuthenticated } from '@/auth/AuthUtils';
import { switchActiveSpace } from '@/auth/switchActiveSpace';
import { queryClient } from '@/queryClient';

export type PushPermission = 'granted' | 'denied' | 'prompt' | 'unsupported';

/** What the API puts next to `aps` in every push; see `PushPayloadData` there. */
export type PushTapData = {
	eventType?: string;
	notificationId?: number | null;
	userGroupId?: number | null;
	target?: string | null;
};

type Navigate = (to: string) => unknown;

export const usePushPermission = create<{ permission: PushPermission | null }>(() => ({ permission: null }));

/** A tapped push whose content opens in a modal rather than on a page. */
export const usePushOpenedNotification = create<{ notificationId: number | null }>(() => ({ notificationId: null }));

/**
 * iOS only for now. On Android, `register()` without a Firebase config crashes the app, and the
 * API has no FCM sender yet.
 */
export function isPushSupported(): boolean {
	return Capacitor.getPlatform() === 'ios';
}

function toPushPermission(state: PermissionState): PushPermission {
	if (state === 'granted' || state === 'denied') {
		return state;
	}
	return 'prompt';
}

export async function refreshPushPermission(): Promise<PushPermission> {
	if (!isPushSupported()) {
		usePushPermission.setState({ permission: 'unsupported' });
		return 'unsupported';
	}
	const { receive } = await PushNotifications.checkPermissions();
	const permission = toPushPermission(receive);
	usePushPermission.setState({ permission });
	return permission;
}

/** Shows the system prompt, which iOS only ever shows once; later calls just report the answer. */
export async function requestPushPermission(): Promise<PushPermission> {
	if (!isPushSupported()) {
		return 'unsupported';
	}
	const { receive } = await PushNotifications.requestPermissions();
	const permission = toPushPermission(receive);
	usePushPermission.setState({ permission });
	if (permission === 'granted') {
		await PushNotifications.register();
	}
	return permission;
}

/** Hands this phone's token to the API for the current session, if the user already allowed pushes. */
export async function registerPushIfGranted(): Promise<void> {
	if (!isPushSupported() || !isAuthenticated()) {
		return;
	}
	if ((await refreshPushPermission()) === 'granted') {
		await PushNotifications.register();
	}
}

async function sendToken(token: string): Promise<void> {
	if (!isAuthenticated()) {
		return;
	}
	const appInfo = await getNativeAppInfo();
	await pushDeviceRegister({ token, platform: 'ios', appVersion: appInfo?.version });
}

export async function handlePushTap(data: PushTapData, navigate: Navigate): Promise<void> {
	if (!isAuthenticated()) {
		return;
	}

	const notificationId = Number(data.notificationId) || null;
	const userGroupId = Number(data.userGroupId) || null;
	const target = typeof data.target === 'string' && data.target.startsWith('/') ? data.target : null;

	if (notificationId) {
		void notificationUpdate(notificationId, { read: true })
			.then(() => queryClient.invalidateQueries({ queryKey: getNotificationFindQueryKey() }))
			.catch(() => undefined);
	}

	if (userGroupId && userGroupId !== getSession()?.userGroupId) {
		try {
			await switchActiveSpace(userGroupId);
			window.location.assign(target ?? '/dashboard');
			return;
		} catch {
			// The user left that space since the push was sent; show what we can in the current one.
		}
	}

	if (data.eventType === 'rule_notification' && notificationId) {
		usePushOpenedNotification.setState({ notificationId });
		return;
	}

	if (target) {
		void navigate(target);
	}
}

/**
 * Installs the push listeners before the first render, so a tap that cold-starts the app is not
 * lost. Never prompts: the Notifications settings page and `PushPrompt` ask when it makes sense.
 */
export function installPushNotifications(navigate: Navigate): void {
	if (!isPushSupported()) {
		return;
	}

	void PushNotifications.addListener('registration', ({ value }) => {
		void sendToken(value).catch((error: unknown) => console.warn('Failed to register push token', error));
	}).catch(() => undefined);

	void PushNotifications.addListener('registrationError', ({ error }) => {
		console.warn('Push registration failed', error);
	}).catch(() => undefined);

	void PushNotifications.addListener('pushNotificationReceived', () => {
		void queryClient.invalidateQueries({ queryKey: getNotificationFindQueryKey() });
	}).catch(() => undefined);

	void PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
		void handlePushTap((notification.data ?? {}) as PushTapData, navigate);
	}).catch(() => undefined);
}
